/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Autocomplete,
  Box,
  Chip,
  List,
  ListItem,
  IconButton,
  FormLabel,
} from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import { FieldArray, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectNotification } from 'modules/common/notifications/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { notificationActions } from 'modules/common/notifications/slice';
import { addVisualValidation } from 'modules/visual/validation/visual-form-validation';
import ERROR_TYPES from 'modules/common/constants/error-types';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import { visualActions } from 'modules/visual/slice';
import { TAG_LIST } from 'modules/visual/constants';
import { selectReportPagesDropdownList, selectReportsDropdownList } from 'modules/visual/selectors';
import { Alert, TextField } from '../style';
/**
 * Form to create visual
 * @param {*} param0
 * @returns
 */
const AddVisualForm = ({ open, onClose }) => {
  const dispatch = useDispatch();
  //
  const notification = useSelector(selectNotification);
  const organizationId = useSelector(selectOrganizationId);
  const reportsDropDownList = useSelector(selectReportsDropdownList);
  const reportPagesDropDownList = useSelector(selectReportPagesDropdownList);
  //
  const modalAction = (action) => {
    if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
    onClose(action);
  };
  //
  const loadPageNames = (value) => {
    //
    dispatch(
      visualActions.getReportPages({
        organizationId,
        reportId: value.id,
      })
    );
  };
  //
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      toast(notification?.message, { type: TOAST_TYPES.SUCCESS });
      modalAction(false);
    }
  }, [notification]);
  const initialValues = {
    name: '',
    pageName: '',
    reportId: '',
    tags: [],
    order: '',
    description: '',
    agentGuidance: [],
    currentGuidance: '',
    currentTags: [],
  };
  //
  const onSubmitAddVisual = async (values) => {
    const valueObj = {
      name: values.name,
      reportId: values.report.id,
      reportName: values.report.label,
      pageId: values.page.id,
      pageName: values.page.label,
      tags: values.tags,
      order: values.order,
      description: values.description,
      agentGuidance: values.agentGuidance,
    };
    dispatch(visualActions.createVisuals({ ...valueObj, organizationId }));
  };
  //
  const closeAddVisualModal = () => {
    dispatch(visualActions.resetReportPages());
    modalAction(false);
  };
  //
  return (
    <Dialog open={open} onClose={() => modalAction(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Add Visual</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={addVisualValidation}
          onSubmit={onSubmitAddVisual}
        >
          {({
            handleSubmit,
            handleBlur,
            handleChange,
            setFieldValue,
            isSubmitting,
            touched,
            values,
            errors,
            setFieldError,
            setFieldTouched,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                <Alert mt={2} mb={3} severity={notification?.type}>
                  {notification?.message}
                </Alert>
              )}
              <TextField
                name="name"
                value={values.name}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
                my={2}
                label="Visual Name"
                size="small"
              />
              <Autocomplete
                size="small"
                value={values.report}
                fullWidth
                disableClearable
                name="report"
                limitTags={3}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={reportsDropDownList || []}
                onChange={(e, value) => {
                  setFieldValue('report', value);
                  loadPageNames(value);
                }}
                onBlur={handleBlur}
                renderInput={(params) => (
                  <TextField
                    error={Boolean(touched.report && errors.report)}
                    helperText={touched.report && errors.report}
                    label="Report"
                    size="small"
                    {...params}
                    fullWidth
                    my={2}
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: 0,
                    height: 'fit-content',
                  },
                }}
              />
              <Autocomplete
                size="small"
                value={values.report}
                fullWidth
                disableClearable
                name="page"
                limitTags={3}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={reportPagesDropDownList || []}
                onChange={(e, value) => {
                  setFieldValue('page', value);
                }}
                onBlur={handleBlur}
                renderInput={(params) => (
                  <TextField
                    error={Boolean(touched.page && errors.page)}
                    helperText={touched.page && errors.page}
                    label="Page Name"
                    size="small"
                    {...params}
                    fullWidth
                    my={2}
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: 0,
                    height: 'fit-content',
                  },
                }}
              />
              <Autocomplete
                size="small"
                value={values.tags}
                fullWidth
                multiple
                disableClearable
                disableCloseOnSelect
                name="tags"
                limitTags={3}
                isOptionEqualToValue={(option, value) => option === value}
                options={TAG_LIST || []}
                onChange={(e, value) => setFieldValue('tags', value)}
                onBlur={handleBlur}
                renderInput={(params) => (
                  <TextField
                    error={Boolean(touched.tags && errors.tags)}
                    helperText={touched.tags && errors.tags}
                    label="Tags"
                    size="small"
                    {...params}
                    fullWidth
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: 0,
                    height: 'fit-content',
                  },
                  marginY: 2,
                }}
              />
              <TextField
                type="number"
                name="order"
                value={values.order}
                error={Boolean(touched.order && errors.order)}
                helperText={touched.order && errors.order}
                onBlur={handleBlur}
                onChange={handleChange}
                fullWidth
                label="Order"
                my={2}
                size="small"
              />
              <TextField
                name="description"
                value={values.description}
                onBlur={handleBlur}
                onChange={handleChange}
                fullWidth
                label="Description"
                size="small"
                multiline
                minRows={4}
                my={2}
                maxRows={10}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 'auto',
                  },
                }}
              />
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: '100px',
                  backgroundColor: '#fafafa',
                }}
              >
                <FormLabel sx={{ my: 2 }}>Agent Guidance</FormLabel>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', my: 2 }}>
                  <TextField
                    fullWidth
                    name="currentGuidance"
                    label="Prompt"
                    value={values.currentGuidance}
                    onChange={(e) => setFieldValue('currentGuidance', e.target.value)}
                    error={touched.currentGuidance && Boolean(errors.currentGuidance)}
                    helperText={touched.currentGuidance && errors.currentGuidance}
                  />
                  <Autocomplete
                    multiple
                    fullWidth
                    limitTags={3}
                    options={['semantic_audit_trend', 'interpreter_agent', 'tour_agent']}
                    value={values.currentTags}
                    onChange={(e, newValue) => setFieldValue('currentTags', newValue)}
                    renderTags={(value, getTagProps) =>
                      value?.map((option, idx) => (
                        <Chip label={option} size="small" {...getTagProps({ idx })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Press Enter to add"
                        size="small"
                      />
                    )}
                    componentsProps={{
                      paper: {
                        sx: {
                          width: '95%',
                          marginLeft: '5%',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: 0,
                        height: 'fit-content',
                      },
                    }}
                  />

                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (!values.currentGuidance || values.currentGuidance.trim() === '') {
                        setFieldTouched('currentGuidance', true);
                        setFieldError('currentGuidance', 'Guidance is required to add an entry');
                        return;
                      }

                      const newEntry = {
                        prompt: values.currentGuidance,
                        tags: values.currentTags,
                      };

                      setFieldValue('agentGuidance', [newEntry, ...values.agentGuidance]);
                      setFieldValue('currentGuidance', '');
                      setFieldValue('currentTags', []);
                      setFieldError('currentGuidance', undefined);
                      setFieldTouched('currentGuidance', false);
                    }}
                  >
                    Add
                  </Button>
                </Box>
                <FieldArray name="agentGuidance">
                  {({ remove }) => (
                    <List>
                      {values.agentGuidance.map((item, index) => (
                        <ListItem
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          secondaryAction={
                            <IconButton color="error" edge="end" onClick={() => remove(index)}>
                              <DeleteOutlined />
                            </IconButton>
                          }
                          sx={{
                            borderBottom: '1px solid #eee',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                          }}
                        >
                          <FormLabel>{item?.prompt}</FormLabel>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {item?.tags.map((tag, tIdx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Chip key={tIdx} label={tag} size="small" />
                            ))}
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </FieldArray>
              </Box>
              <DialogActions>
                <Button onClick={closeAddVisualModal} variant="contained" color="primary">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                  Save
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
//
export default AddVisualForm;
